/**
 * DRR-092: Scan Status Badge Component
 *
 * Displays ClamAV scan status as a color-coded Mantine badge.
 * Used in the Legacy Archive file browser table.
 *
 * Status → Color:
 * - CLEAN → green
 * - MACRO_DETECTED → yellow
 * - INFECTED → red
 * - UNSCANNED → gray
 */

import { Badge, Tooltip } from '@mantine/core';
import {
  IconShieldCheck,
  IconAlertTriangle,
  IconShieldX,
  IconClock,
} from '@tabler/icons-react';
import type { ScanStatus } from '@/generated/graphql';

interface ScanStatusBadgeProps {
  status: ScanStatus;
  scanDate?: string | null;
  clamavVersion?: string | null;
}

interface StatusConfigEntry {
  color: string;
  label: string;
  icon: typeof IconShieldCheck;
}

const DEFAULT_STATUS: StatusConfigEntry = { color: 'gray', label: 'Pending', icon: IconClock };

const STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  CLEAN: { color: 'green', label: 'Clean', icon: IconShieldCheck },
  MACRO_DETECTED: {
    color: 'yellow',
    label: 'Macros',
    icon: IconAlertTriangle,
  },
  INFECTED: { color: 'red', label: 'Blocked', icon: IconShieldX },
  UNSCANNED: DEFAULT_STATUS,
};

export function ScanStatusBadge({
  status,
  scanDate,
  clamavVersion,
}: ScanStatusBadgeProps) {
  const config = STATUS_CONFIG[status] ?? DEFAULT_STATUS;
  const Icon = config.icon;

  const tooltipLabel = scanDate
    ? `Scanned ${new Date(scanDate).toLocaleDateString()} (ClamAV ${clamavVersion ?? 'unknown'})`
    : 'Scan pending';

  return (
    <Tooltip label={tooltipLabel} withArrow>
      <Badge
        color={config.color}
        variant="light"
        leftSection={<Icon size={14} />}
      >
        {config.label}
      </Badge>
    </Tooltip>
  );
}
